import React from 'react';
import { Table, Form } from 'react-bootstrap';

const ColumnMapper = ({ columnMappings, systemFields, detectedUnits, exampleValues, onChange }) => {

  const handleFieldChange = (index, newField) => {
    // Check if this field is already mapped by another column
    if (newField !== '') {
      const alreadyMapped = columnMappings.findIndex((m, i) =>
        i !== index && m.systemField === newField
      );

      if (alreadyMapped !== -1) {
        alert(`The field "${newField}" is already mapped to column "${columnMappings[alreadyMapped].originalName}". Please choose a different field or clear the other mapping first.`);
        return;
      }
    }

    const updated = [...columnMappings];
    const field = systemFields.find(f => f.name === newField);

    // Target unit is ALWAYS the field's default (DB storage unit)
    updated[index] = {
      ...updated[index],
      systemField: newField,
      targetUnit: field ? field.defaultUnit : 'none',
      requiresConversion: field && updated[index].sourceUnit !== 'none' && updated[index].sourceUnit !== field.defaultUnit
    };

    onChange(updated);
  };

  const handleSourceUnitChange = (index, newUnit) => {
    const updated = [...columnMappings];
    const targetUnit = updated[index].targetUnit;

    updated[index] = {
      ...updated[index],
      sourceUnit: newUnit,
      requiresConversion: newUnit !== 'none' && targetUnit !== 'none' && newUnit !== targetUnit
    };

    onChange(updated);
  };

  const getUnitOptionsForField = (fieldName) => {
    const field = systemFields.find(f => f.name === fieldName);
    return field ? field.units : ['none'];
  };

  return (
    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
      <Table striped bordered hover size="sm">
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
          <tr>
            <th style={{ width: '25%' }}>CSV Column</th>
            <th style={{ width: '30%' }}>System Field</th>
            <th style={{ width: '20%' }}>Source Unit</th>
            <th style={{ width: '25%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {columnMappings.map((mapping, index) => {
            const selectedField = systemFields.find(f => f.name === mapping.systemField);
            const availableUnits = getUnitOptionsForField(mapping.systemField);

            return (
              <tr key={index}>
                <td>
                  <strong>{mapping.originalName}</strong>
                  {exampleValues && exampleValues[mapping.originalName] && (
                    <small className="text-muted d-block">
                      Example: {exampleValues[mapping.originalName]}
                    </small>
                  )}
                  {detectedUnits[mapping.originalName] && (
                    <small className="text-muted d-block">
                      Detected: [{detectedUnits[mapping.originalName]}]
                    </small>
                  )}
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={mapping.systemField}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                  >
                    <option value="">-- Ignore Field --</option>
                    {systemFields.map(field => (
                      <option key={field.name} value={field.name}>
                        {field.name} {field.required && '*'}
                      </option>
                    ))}
                  </Form.Select>
                  {selectedField && (
                    <small className="text-muted d-block">
                      {selectedField.description}
                    </small>
                  )}
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={mapping.sourceUnit}
                    onChange={(e) => handleSourceUnitChange(index, e.target.value)}
                    disabled={!mapping.systemField}
                  >
                    {availableUnits.map(unit => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>
                  {mapping.requiresConversion ? (
                    <div>
                      <span className="badge bg-warning text-dark">
                        Converting: {mapping.sourceUnit} → {mapping.targetUnit}
                      </span>
                      <small className="d-block text-muted">
                        DB stores in {mapping.targetUnit}
                      </small>
                    </div>
                  ) : mapping.systemField ? (
                    <div>
                      <span className="badge bg-success">
                        No conversion needed
                      </span>
                      <small className="d-block text-muted">
                        DB stores in {mapping.targetUnit}
                      </small>
                    </div>
                  ) : (
                    <span className="badge bg-secondary">
                      Field ignored
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="mt-3 p-3 bg-light border rounded">
        <small>
          <strong>Instructions:</strong>
          <ul className="mb-0 mt-2">
            <li>Map each CSV column to a system field, or select "Ignore Field" to skip it</li>
            <li><strong>Required fields</strong> are marked with * (Carry and Club minimum)</li>
            <li><strong>Source Unit</strong>: Select the unit used in your CSV file</li>
            <li><strong>Database Storage</strong>: All data is converted to standard units: <strong>yards</strong> (distance), <strong>mph</strong> (speed), <strong>degrees</strong> (angles), <strong>rpm</strong> (spin)</li>
            <li>Date field should be ignored - dates are extracted from filenames</li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default ColumnMapper;
